import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Tab, Col, Nav, NavItem } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseStore } from '../../store/providers/Franchise';
import Permission from '../../components/Permission/Permission';

import { breadcrumbRemoveAllRoutes } from '../../store/actions/breadcrumb';

import * as constants from '../../constants';
import BranchOfficesTab from './Tabs/BranchOfficesTab';

// TODO: Uncomment for production

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const ExpiringFranchisesWidget = React.lazy(() => import('./Widgets/ExpiringFranchises'));
const AgentTransactionsTab = React.lazy(() => import('./Tabs/AgentTransactionsTab'));
const CentralizedServicesTab = React.lazy(() => import('./Tabs/CentralizedServicesTab'));

function Dashboard() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { optionRecords, clearData, data } = useFranchiseStore();

    const pagePermission = constants.PERMISSION_DASHBOARD;
    const [hasPermission, setHasPermission] = useState(false);
    const [, setCanEdit] = useState(false);

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [franchiseOptions, setFranchiseOptions] = useState();
    const [currentFranchise, setCurrentFranchise] = useState(1);
    const [selectedFranchise, setSelectedFranchise] = useState(null);
    const [selectedTab, setSelectedTab] = useState('agentTransactions');
    const [selectedWidgetTab, setWidgetSelectedTab] = useState('expiringFranchisesWidget');

    const selectFranchiseHandler = (event) => {
        setCurrentFranchise(event.value);
    };

    const tabSelectedHandler = (tabKey) => {
        setSelectedTab(tabKey);
    };

    const tabWidgetSelectedHandler = (tabKey) => {
        setWidgetSelectedTab(tabKey);
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }
        }

        setIsBusy(_.isEmpty(franchiseOptions));
    }, [data, addAlert, clearData, franchiseOptions]);

    useEffect(() => {
        if (hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbRemoveAllRoutes());

        optionRecords();

        setIsMounted(true);
    }, [hasPermission, isMounted, dispatch, optionRecords]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveAllRoutes());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isMounted && _.isEmpty(franchiseOptions) && data.scope === 'options' && data.status === 'success') {
            let options = [];

            data.body = _.orderBy(data.body, [data.body.stateCode]);

            data.body.forEach((franchise) => {
                if (franchise.demo) { return; }

                let include = false;

                if (_.isEmpty(franchise.terminatedOn)) { include = true; }

                if (!include) {
                    const now = moment(new Date());
                    const days30 = moment(now).subtract(30, 'days');

                    if (moment(franchise.terminatedOn) > days30) { include = true; }
                } else {
                    options.push({ value: franchise.id, label: `${franchise.name} (${franchise.stateCode}; ${franchise.instance.toUpperCase()})` });
                }
            });

            options = [
                { value: '0', label: 'All Active (Corp, Non Corp, BrokerOps)' },
                { value: '-3', label: 'Corporate, Non-Corporate' },
                { value: '-1', label: 'Corporate Only' },
                { value: '-2', label: 'Non-Corporate Only' },
                { value: '-4', label: 'BrokerOps Only' }
            ].concat(options);

            setFranchiseOptions(options);
        }
    }, [isMounted, data, franchiseOptions]);

    useEffect(() => {
        if (franchiseOptions?.length && currentFranchise != null) {
            const match = franchiseOptions.find(f => f.value === currentFranchise);
            setSelectedFranchise(match || null);
        }
    }, [franchiseOptions, currentFranchise]);

    return (
        <>
            <SideMenu activeKey="dashboard" />
            <div className="content-body">
                <Permission pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <>
                        <div style={{ paddingRight: '15px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Tab.Container id="franchisesWidget" defaultActiveKey="expiringFranchisesWidget" activeKey={selectedWidgetTab} onSelect={tabWidgetSelectedHandler}>
                                <Row className="clearfix">
                                    <Col sm={12}>
                                        <Nav bsStyle="pills">
                                            <NavItem eventKey="expiringFranchisesWidget" disabled={isBusy}>Expiring Instances</NavItem>
                                            <NavItem eventKey="branchOfficesWidget" disabled={isBusy}>Branch Offices</NavItem>
                                        </Nav>
                                    </Col>
                                    <Col sm={12}>
                                        <Tab.Content animation>
                                            <Tab.Pane eventKey="expiringFranchisesWidget">
                                                {
                                                    <ExpiringFranchisesWidget />
                                                }
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="branchOfficesWidget">
                                                {
                                                    (!isBusy && selectedWidgetTab === 'branchOfficesWidget') && <BranchOfficesTab />
                                                }
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Col>
                                </Row>
                            </Tab.Container>

                            <Tab.Container id="franchiseMetrics" defaultActiveKey="agentTransactions" activeKey={selectedTab} onSelect={tabSelectedHandler}>
                                <Row className="clearfix">
                                    <Col sm={12}>
                                        <Nav bsStyle="pills">
                                            <NavItem eventKey="agentTransactions" disabled={isBusy}>Agent Transactions</NavItem>
                                            <NavItem eventKey="centralizedServices" disabled={isBusy}>Centralized Services</NavItem>
                                        </Nav>
                                    </Col>
                                    <Col sm={12}>
                                        <Tab.Content animation>
                                            <Tab.Pane eventKey="agentTransactions">
                                                                            <PageHeader title="Dashboard" subTitle="All charts are current up to yesterday night." style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px' }}>
                                <div style={{ display: 'flex', whiteSpace: 'nowrap', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ marginRight: '10px' }}>Select Instance:</span>
                                    <div style={{ width: '400px' }}>
                                        <Select
                                            id="franchiseId" style={{ width: '100%' }}
                                            styles={{
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 99,
                                                })
                                            }}
                                            isDisabled={isBusy} onChange={(e) =>  { setSelectedFranchise(e); selectFranchiseHandler(e); }} value={selectedFranchise} isLoading={isBusy} isClearable={false} isSearchable={true} options={franchiseOptions} />
                                    </div>
                                </div>
                            </PageHeader>
                                                {
                                                    <AgentTransactionsTab franchiseId={currentFranchise} />
                                                }
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="centralizedServices">
                                                {
                                                    (!isBusy && selectedTab === 'centralizedServices') && <CentralizedServicesTab />
                                                }
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Col>
                                </Row>
                            </Tab.Container>
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Dashboard));
